/**
 * SB6-ANA-CACHE-03 — storage persistence + cache-control actions.
 *
 * Three concerns the storage UI + pin/download flows share:
 *
 * 1. **`navigator.storage.persist()`** — requested ONCE, on the first pin or the
 *    first explicit dataset download (story §Persistence). Granting exempts our
 *    origin from eviction under disk pressure (the browser otherwise may drop
 *    Cache Storage on a full disk — the Friday→Monday risk). The grant/deny
 *    state is surfaced in the storage UI via a tiny subscribable store.
 * 2. **`navigator.storage.estimate()`** — total origin usage/quota for the UI's
 *    headline number.
 * 3. **Cache-control actions** — `clearDataset` (one dataset's bytes + manifest)
 *    and `clearAllImageCache` (the whole bucket + every manifest record), plus
 *    `setPinned` which flips the manifest flag AND lazily requests persistence.
 *
 * Everything degrades gracefully when the StorageManager API is unavailable.
 */
import { imageCache } from "./imageCache.js";
import { cacheManifest } from "./cacheManifest.js";

/**
 * The StorageManager methods, typed as OPTIONAL. The DOM lib declares them as
 * always-present, but they're genuinely absent in insecure contexts / older
 * browsers / jsdom — so we access `navigator.storage` through this loose shape
 * to keep the runtime guards (which TS would otherwise flag as "always falsy").
 */
interface MaybeStorageManager {
  persist?: () => Promise<boolean>;
  persisted?: () => Promise<boolean>;
  estimate?: () => Promise<{ usage?: number; quota?: number }>;
}

function getStorageManager(): MaybeStorageManager | null {
  if (typeof navigator === "undefined") return null;
  const sm = (navigator as unknown as { storage?: MaybeStorageManager })
    .storage;
  return sm ?? null;
}

// ---------------------------------------------------------------------------
// Persistence state store
// ---------------------------------------------------------------------------

export type PersistState = "unknown" | "granted" | "denied" | "unsupported";

class PersistenceStore {
  private state: PersistState = "unknown";
  /** Latched once we've fired the one-time persist() request. */
  private requested = false;
  private listeners = new Set<() => void>();

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  getState = (): PersistState => this.state;

  private set(next: PersistState): void {
    if (this.state === next) return;
    this.state = next;
    for (const fn of this.listeners) fn();
  }

  /**
   * Reflect the CURRENT persisted status without prompting (idempotent). Used by
   * the storage UI on open so the readout is honest even before the first
   * pin/download triggers the request.
   */
  async refresh(): Promise<void> {
    const sm = getStorageManager();
    if (!sm?.persisted) {
      this.set("unsupported");
      return;
    }
    try {
      const persisted = await sm.persisted();
      this.set(persisted ? "granted" : this.requested ? "denied" : "unknown");
    } catch {
      this.set("unsupported");
    }
  }

  /**
   * Request persistence ONCE. Subsequent calls are no-ops (the browser won't
   * re-prompt anyway). Safe to call from both the first-pin and first-download
   * paths — whichever happens first wins.
   */
  async requestOnce(): Promise<void> {
    if (this.requested) return;
    this.requested = true;
    const sm = getStorageManager();
    if (!sm?.persist) {
      this.set("unsupported");
      return;
    }
    try {
      // Already granted? Don't re-request.
      if (sm.persisted && (await sm.persisted())) {
        this.set("granted");
        return;
      }
      const granted = await sm.persist();
      this.set(granted ? "granted" : "denied");
    } catch {
      this.set("unsupported");
    }
  }

  /** @internal — reset (tests). */
  __reset(): void {
    this.state = "unknown";
    this.requested = false;
  }
}

export const persistenceStore = new PersistenceStore();

// ---------------------------------------------------------------------------
// Usage estimate
// ---------------------------------------------------------------------------

export interface StorageEstimateResult {
  usage: number | null;
  quota: number | null;
}

/** Read `navigator.storage.estimate()`; null fields when unavailable. */
export async function readStorageEstimate(): Promise<StorageEstimateResult> {
  const sm = getStorageManager();
  if (!sm?.estimate) {
    return { usage: null, quota: null };
  }
  try {
    const est = await sm.estimate();
    return {
      usage: typeof est.usage === "number" ? est.usage : null,
      quota: typeof est.quota === "number" ? est.quota : null,
    };
  } catch {
    return { usage: null, quota: null };
  }
}

// ---------------------------------------------------------------------------
// Cache-control actions
// ---------------------------------------------------------------------------

/**
 * Set a dataset's pinned flag. On the FIRST pin (across the session) this also
 * requests `navigator.storage.persist()` once. Unpinning re-enters the dataset
 * into the LRU budget (AC3) — handled by the manifest flag the evictor reads.
 */
export async function setPinned(
  dataAcquisitionId: string,
  pinned: boolean,
): Promise<void> {
  cacheManifest.setPinned(dataAcquisitionId, pinned);
  if (pinned) await persistenceStore.requestOnce();
}

/**
 * Clear exactly one dataset: delete its cached image bytes, then drop its
 * manifest record (AC4 — "clear removes exactly that dataset's entries").
 */
export async function clearDataset(dataAcquisitionId: string): Promise<void> {
  const entry = cacheManifest.get(dataAcquisitionId);
  if (entry) await imageCache.deleteKeys(entry.imageKeys);
  cacheManifest.removeDataset(dataAcquisitionId);
}

/**
 * Clear ALL image cache: drop the whole `wp-images-v1` bucket and every manifest
 * record (AC5). The currently-open dataset keeps working — it simply re-fetches
 * over the network on the next image resolve.
 */
export async function clearAllImageCache(): Promise<void> {
  await imageCache.clearAll();
  cacheManifest.clearAll();
}
